import { Button } from '@heidi/design-system/button';
import { Chip } from '@heidi/design-system/chip';
import { Textarea } from '@heidi/design-system/textarea';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { X } from 'lucide-react';
import * as m from 'motion/react-m';
import { type MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
  DictationFeedbackCallbacks,
  DictationFeedbackContext,
  DictationFeedbackReason,
  DictationFeedbackSubmission,
} from '../../contracts/dictation-feedback.types';
import {
  trackDictationFeedbackNotNowClicked,
  trackDictationFeedbackReasonPickerViewed,
  trackDictationLogsFeedbackReasonPickerViewed,
} from '../../data/analytics/trackDictationFeedbackAnalytics';
import { trackFeedbackAnalyticsSafely } from '../../data/analytics/trackFeedbackAnalyticsSafely';
import { bubbleEnterExitAnimation } from '../shared/animation';
import { dictationFeedbackReasonPickerMessages as messages } from './dictation-feedback-reason-picker.messages';

/** Owns the short-lived transition from the rating prompt into this picker. */
export const useDictationFeedbackReasonFlow = ({
  active,
  onRequestNegativeFeedback,
}: Pick<DictationFeedbackCallbacks, 'onRequestNegativeFeedback'> & {
  active: boolean;
}) => {
  const [isReasonPickerOpen, setIsReasonPickerOpen] = useState(false);

  useEffect(() => {
    if (!active) setIsReasonPickerOpen(false);
  }, [active]);

  const requestNegativeFeedback = useCallback(
    (context: DictationFeedbackContext) => {
      setIsReasonPickerOpen(true);
      onRequestNegativeFeedback?.(context);
    },
    [onRequestNegativeFeedback]
  );

  const closeReasonPicker = useCallback(() => setIsReasonPickerOpen(false), []);

  return { isReasonPickerOpen, requestNegativeFeedback, closeReasonPicker };
};

const reasonOptions: ReadonlyArray<{
  value: DictationFeedbackReason;
  message: (typeof messages)[keyof typeof messages];
}> = [
  { value: 'wrong_word_or_name', message: messages.wrongWordOrName },
  { value: 'command_issue', message: messages.commandIssue },
  { value: 'custom_vocab_issue', message: messages.customVocabIssue },
  { value: 'content_missing', message: messages.contentMissing },
  { value: 'number_dose_or_date_wrong', message: messages.numberDoseOrDateWrong },
  { value: 'other', message: messages.other },
];

const OTHER_DETAIL_MAX_LENGTH = 500;

interface DictationFeedbackReasonPickerProps extends DictationFeedbackContext {
  /** Dismisses the picker without submitting feedback. */
  onCancel: () => void;
  onSubmit?: (
    feedback: Extract<DictationFeedbackSubmission, { score: 'negative' }>
  ) => void | Promise<void>;
  /** Submission state owned by the host's feedback flow. */
  isSubmitting?: boolean;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseMove?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  /** Lets the desktop host make its non-activating panel key while typing. */
  onOtherDetailEditingChange?: (editing: boolean) => void | Promise<void>;
  className?: string;
  surface?: 'widget' | 'logs';
}

/** The multi-select follow-up shown after a user rates dictation output poorly. */
export const DictationFeedbackReasonPicker = ({
  dictationId,
  platform,
  isSubmitting = false,
  onSubmit,
  onCancel,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onOtherDetailEditingChange,
  className,
  surface = 'widget',
}: DictationFeedbackReasonPickerProps) => {
  const intl = useIntl();
  const [selectedReasons, setSelectedReasons] = useState<DictationFeedbackReason[]>([]);
  const [otherDetail, setOtherDetail] = useState('');
  const showsOtherDetail = selectedReasons.includes('other');
  const otherDetailRef = useRef<HTMLTextAreaElement>(null);
  const keyboardFocusRequestRef = useRef<Promise<void>>(Promise.resolve());
  const editingAppliedRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (surface === 'widget') {
      trackFeedbackAnalyticsSafely('reason_picker_viewed', () =>
        trackDictationFeedbackReasonPickerViewed()
      );
    } else {
      trackFeedbackAnalyticsSafely('logs_reason_picker_viewed', () =>
        trackDictationLogsFeedbackReasonPickerViewed()
      );
    }
  }, [surface]);

  const dismissWithoutReasons = () => {
    if (surface === 'widget') {
      trackFeedbackAnalyticsSafely('not_now_clicked', () =>
        trackDictationFeedbackNotNowClicked()
      );
    }
    onCancel();
  };

  const setOtherDetailEditing = useCallback(
    (editing: boolean) => {
      if (!onOtherDetailEditingChange) {
        return keyboardFocusRequestRef.current;
      }

      const request = keyboardFocusRequestRef.current
        .then(async () => {
          if (editingAppliedRef.current === editing) return;

          await onOtherDetailEditingChange(editing);
          editingAppliedRef.current = editing;
        })
        .catch((error) => {
          console.warn('Could not change dictation widget keyboard focus', error);
        });
      keyboardFocusRequestRef.current = request;
      return request;
    },
    [onOtherDetailEditingChange]
  );

  useEffect(() => {
    if (!showsOtherDetail || !onOtherDetailEditingChange) return;

    let active = true;
    void setOtherDetailEditing(true).then(() => {
      if (active) otherDetailRef.current?.focus();
    });

    return () => {
      active = false;
      void setOtherDetailEditing(false);
    };
  }, [onOtherDetailEditingChange, setOtherDetailEditing, showsOtherDetail]);

  const toggleReason = (reason: DictationFeedbackReason) => {
    setSelectedReasons((current) =>
      current.includes(reason)
        ? current.filter((selectedReason) => selectedReason !== reason)
        : [...current, reason]
    );
  };

  const submitFeedback = async () => {
    if (!onSubmit || isSubmitting || selectedReasons.length === 0) return;

    try {
      const userFeedback = showsOtherDetail ? otherDetail.trim() : '';
      await onSubmit({
        score: 'negative',
        reasons: selectedReasons,
        dictationId,
        platform,
        ...(userFeedback ? { userFeedback } : {}),
      });
    } catch {
      // The host owns failure state and keeps the picker actionable for retry.
    }
  };

  return (
    <m.div
      data-testid="dictation-feedback-reason-picker"
      data-widget-hit-surface
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className={cn(
        'relative flex shrink-0 flex-col items-start justify-center gap-3',
        surface === 'widget'
          ? 'w-[375px] overflow-hidden rounded-2xl border border-border bg-surface-accent/80 px-4 pb-3 pt-4 backdrop-blur-sm newBrandDark:bg-surface-elevated/80'
          : 'w-full bg-transparent',
        surface === 'widget' && (showsOtherDetail ? 'min-h-[263px]' : 'h-[182px]'),
        className
      )}
      {...bubbleEnterExitAnimation}
    >
      {surface === 'logs' && (
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          className="absolute end-0 top-0"
          disabled={isSubmitting}
          aria-label={intl.formatMessage(messages.close)}
          onClick={dismissWithoutReasons}
        >
          <X aria-hidden="true" />
        </Button>
      )}
      <TypographyP2Bold
        className={cn(
          'min-w-full leading-none [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]',
          surface === 'logs' ? 'text-text-primary' : 'text-text-secondary'
        )}
      >
        <FormattedMessage {...messages.title} />
      </TypographyP2Bold>

      <div
        data-testid="dictation-feedback-reasons"
        className={cn(
          'flex w-full flex-wrap content-center items-center gap-1.5 pb-2',
          surface === 'widget' ? 'h-[92px]' : 'min-h-[92px]'
        )}
      >
        {reasonOptions.map(({ value, message }) => {
          const selected = selectedReasons.includes(value);

          return (
            <Chip
              key={value}
              aria-pressed={selected}
              className={
                selected
                  ? 'bg-background-button-primary text-text-primary-invert hover:bg-background-button-primary-hover active:bg-background-button-primary-pressed'
                  : undefined
              }
              onClick={() => toggleReason(value)}
            >
              <FormattedMessage {...message} />
            </Chip>
          );
        })}
      </div>

      {showsOtherDetail && (
        <Textarea
          ref={otherDetailRef}
          data-testid="dictation-feedback-other-detail"
          aria-label={intl.formatMessage(messages.otherDetailLabel)}
          rows={2}
          className="max-h-[240px] max-w-full resize-y"
          maxLength={OTHER_DETAIL_MAX_LENGTH}
          value={otherDetail}
          onChange={(event) => setOtherDetail(event.target.value)}
          onFocus={() => void setOtherDetailEditing(true)}
          onBlur={() => void setOtherDetailEditing(false)}
          placeholder={
            otherDetail ? undefined : intl.formatMessage(messages.otherDetailPlaceholder)
          }
        />
      )}

      <div className="flex w-full items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="xs"
          disabled={isSubmitting}
          onClick={dismissWithoutReasons}
        >
          <FormattedMessage
            {...(surface === 'logs' ? messages.cancel : messages.notNow)}
          />
        </Button>
        <Button
          size="xs"
          disabled={!onSubmit || selectedReasons.length === 0 || isSubmitting}
          onClick={() => void submitFeedback()}
        >
          <FormattedMessage {...messages.submit} />
        </Button>
      </div>
    </m.div>
  );
};
